import React, { useEffect, useState } from 'react';
import {
    Typography,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Switch,
    Checkbox,
    Box,
    Divider,
    Grid,
    Paper,
    Backdrop,
    CircularProgress,
    Button,
    makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { fetchConsentMetadata, submitConsent, consentActions, updateLegalEntity } from 'src/store/slices/consentSlice';
import { getUser } from 'src/utils/Common';

const useStyles = makeStyles(theme => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    panel: {
        maxWidth: '100%',
        overflowX: 'auto',
        border: '1px solid #eee',
        borderRadius: 2,
        padding: theme.spacing(1),
        background: '#fafafa'
    },
    noScroll: {
        overflow: 'visible'
    },
    gridFix: {
        margin: 0,
        width: '100%'
    },
    header: {
        marginBottom: theme.spacing(3),
        textAlign: 'center'
    },
    submitButton: {
        marginTop: theme.spacing(3),
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    lastAskedOn: {
        color: theme.palette.error.main
    },
    consentNotAskedYet: {
        color: theme.palette.success.main
    }
}));

// System bazlı konfigürasyon
const SYSTEM_CONFIG = {
    Local: {
        thirdPartyLegalEntityId: 6,
    },
    Test: {
        thirdPartyLegalEntityId: 6,
    },
    Live: {
        thirdPartyLegalEntityId: 11,
    },
};

const currentSystem = process.env.REACT_APP_SYSTEM || 'Live';
const currentConfig = SYSTEM_CONFIG[currentSystem] || SYSTEM_CONFIG.Live;

// Third party legal entity için Service consent seçeneği gösterilmez/gönderilmez.
const THIRD_PARTY_LEGAL_ENTITY_ID = currentConfig.thirdPartyLegalEntityId;
const SERVICE_CONSENT_TYPE_ID = 1;

const ConsentCore = ({ ConsumerId }) => {
    const { t } = useTranslation('overview');
    const dispatch = useDispatch();
    const classes = useStyles();
    const {
        legalEntities,
        contacts,
        consentTypes,
        permissionTypes,
        identifier,
        loading,
        error,
        submitSuccess,
        fetchMetaDataError,
        success,
        errors,
    } = useSelector((state) => state.consent);

    const [selectedEntities, setSelectedEntities] = useState([]);
    const [entityConsent, setEntityConsent] = useState({});
    const [initialConsents, setInitialConsents] = useState({ Email: [], Phone: [] });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const SessionUser = getUser();

    const getConsentTypesForEntity = (entity) =>
        entity.LegalEntityId === THIRD_PARTY_LEGAL_ENTITY_ID
            ? consentTypes.filter(x => x.Key !== SERVICE_CONSENT_TYPE_ID)
            : consentTypes;

    useEffect(() => {
        if (ConsumerId) {
            dispatch(fetchConsentMetadata({ consumerId: ConsumerId }));
        }
    }, [ConsumerId, dispatch]);

    useEffect(() => {
        if (
            legalEntities.length > 0 &&
            consentTypes.length > 0 &&
            permissionTypes.length > 0
        ) {
            setEntityConsent(
                legalEntities.reduce((acc, entity) => {
                    acc[entity.Id] = consentTypes.reduce((typeAcc, type) => {
                        typeAcc[type.Key] = permissionTypes.reduce((permAcc, perm) => {
                            if (perm.Label.toLowerCase() === 'email' && contacts.Email) {
                                const emails = contacts.Email.filter(e =>
                                    e.LegalEntityId === entity.Id &&
                                    e.ConsentTypeId === type.Key &&
                                    e.PurposeItemIdentityType === perm.Key
                                );
                                permAcc[perm.Key] = {
                                    enabled: emails.some(e => e.IsActive),
                                    selected: emails.filter(e => e.IsActive).map(e => e.Value)
                                };
                            } else if (contacts.Phone && perm.Label.toLowerCase() !== 'email') {
                                const phones = contacts.Phone.filter(p =>
                                    p.LegalEntityId === entity.Id &&
                                    p.ConsentTypeId === type.Key &&
                                    p.PurposeItemIdentityType === perm.Key
                                );
                                permAcc[perm.Key] = {
                                    enabled: phones.some(p => p.IsActive),
                                    selected: phones.filter(p => p.IsActive).map(p => p.Value)
                                };
                            } else {
                                permAcc[perm.Key] = { enabled: false, selected: [] };
                            }
                            return permAcc;
                        }, {});
                        return typeAcc;
                    }, {});
                    return acc;
                }, {})
            );
        }
    }, [legalEntities, consentTypes, permissionTypes, contacts]);

    useEffect(() => {
        if (legalEntities.length > 0) {
            let defaultSelected = [];

            if (legalEntities.length === 1) {
                // Tek LegalEntity varsa eski mantık: aktif contact'ları olan seçili gelsin
                if (consentTypes.length > 0 && permissionTypes.length > 0) {
                    defaultSelected = legalEntities
                        .filter(entity => {
                            const emails = contacts.Email?.filter(e => e.LegalEntityId === entity.Id && e.IsActive) || [];
                            const phones = contacts.Phone?.filter(p => p.LegalEntityId === entity.Id && p.IsActive) || [];
                            return emails.length > 0 || phones.length > 0;
                        })
                        .map(entity => entity.Id);
                }
            } else {
                // Birden fazla LegalEntity varsa yeni mantık: IsSelected = true olanlar seçili gelsin
                defaultSelected = legalEntities
                    .filter(entity => entity.IsSelected === true)
                    .map(entity => entity.Id);
            }

            setSelectedEntities(defaultSelected);
        }
    }, [legalEntities, contacts, consentTypes, permissionTypes]);

    useEffect(() => {
        if (contacts.Email || contacts.Phone) {
            setInitialConsents({
                Email: contacts.Email || [],
                Phone: contacts.Phone || []
            });
        }
    }, [contacts.Email, contacts.Phone]);

    useEffect(() => {
        if (submitSuccess) {
            const timer = setTimeout(() => {
                dispatch(consentActions.clearSubmitStatus());
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [submitSuccess, dispatch]);

    const singleSelectionTenants = [12, 53]; // Tekli seçim yapılacak TenantId'ler
    const handleEntitySelect = (entityId) => (event) => {
        let checked;
        if (event.target.type === 'checkbox') {
            checked = event.target.checked;
        } else {
            const checkbox = event.currentTarget.querySelector('input[type="checkbox"]');
            checked = checkbox ? !checkbox.checked : false; // Toggle yapıyoruz
        }

        if (typeof checked !== "boolean") return;

        if (singleSelectionTenants.includes(SessionUser.TenantId)) {
            // Sadece bir entity seçilebilir
            setSelectedEntities(checked ? [entityId] : []);
        } else {

            setSelectedEntities((prev) =>
                checked
                    ? [...prev, entityId]
                    : prev.filter((id) => id !== entityId)
            );
        }
    };

    const handlePermissionSwitch = (entityId, consentTypeKey, permissionTypeKey) => (event) => {
        let checked;
        if (event.target.type === 'checkbox') {
            checked = event.target.checked;
        } else {
            const switchInput = event.currentTarget.querySelector('input[type="checkbox"]');
            checked = switchInput ? !switchInput.checked : false;
        }

        if (typeof checked !== "boolean") return;

        setEntityConsent((prev) => ({
            ...prev,
            [entityId]: {
                ...prev[entityId],
                [consentTypeKey]: {
                    ...prev[entityId][consentTypeKey],
                    [permissionTypeKey]: {
                        ...prev[entityId][consentTypeKey][permissionTypeKey],
                        enabled: checked,
                        selected: []
                    }
                }
            }
        }));
    };

    const handleUpdateLegalEntity = () => {
        const selectedLegalEntityIds = selectedEntities
            .map(id => legalEntities.find(e => e.Id === id)?.LegalEntityId)
            .filter(Boolean);
        dispatch(updateLegalEntity({
            consumerId: ConsumerId,
            selected: selectedLegalEntityIds,
            EmployeeId: SessionUser?.TechnicianId
        }));
    };

    const handleContactSelect = (entityId, consentTypeKey, permissionTypeKey, value) => (event) => {
        let checked;
        if (event.target.type === 'checkbox') {
            checked = event.target.checked;
        } else {
            const checkbox = event.currentTarget.querySelector('input[type="checkbox"]');
            checked = checkbox ? !checkbox.checked : false;
        }

        if (typeof checked !== "boolean") return;

        const selected = entityConsent?.[entityId]?.[consentTypeKey]?.[permissionTypeKey]?.selected ?? [];
        setEntityConsent((prev) => ({
            ...prev,
            [entityId]: {
                ...prev[entityId],
                [consentTypeKey]: {
                    ...prev[entityId][consentTypeKey],
                    [permissionTypeKey]: {
                        ...prev[entityId][consentTypeKey][permissionTypeKey],
                        selected: checked
                            ? [...selected, value]
                            : selected.filter((v) => v !== value)
                    }
                }
            }
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        const permissionMap = new Map();

        legalEntities.forEach((entity) => {
            const entityId = entity.Id;
            getConsentTypesForEntity(entity).forEach((consentType) => {
                permissionTypes.forEach((permissionType) => {
                    // Email kontrolü - sadece geçerli email adreslerini işle
                    if (permissionType.Label.toLowerCase() === 'email' && identifier.Email && Array.isArray(identifier.Email)) {
                        const validEmails = identifier.Email.filter(email =>
                            email &&
                            typeof email === 'string' &&
                            email.trim() !== '' &&
                            email.includes('@')
                        );

                        validEmails.forEach((email) => {
                            const emailConsent = entityConsent[entityId]?.[consentType.Key]?.[permissionType.Key];
                            const isSelected = emailConsent?.selected?.includes(email);

                            const key = `${ConsumerId}_${entityId}_${email}_${consentType.Key}_${permissionType.Key}`;
                            permissionMap.set(key, {
                                ConsumerId,
                                Identifier: email,
                                LegalEntityId: entityId,
                                Status: isSelected ? "CONFIRMED" : "WITHDRAWN",
                                PurposeItemIdentityTypes: permissionType.Key,
                                ConsentTypeId: consentType.Key
                            });
                        });
                    }
                    // Telefon kontrolü - sadece geçerli telefon numaralarını işle
                    if (permissionType.Label.toLowerCase() !== 'email' && identifier.Phone && Array.isArray(identifier.Phone)) {
                        const validPhones = identifier.Phone.filter(phone =>
                            phone &&
                            typeof phone === 'string' &&
                            phone.trim() !== ''
                        );

                        validPhones.forEach((phone) => {
                            const phoneConsent = entityConsent[entityId]?.[consentType.Key]?.[permissionType.Key];
                            const isSelected = phoneConsent?.selected?.includes(phone);

                            const key = `${ConsumerId}_${entityId}_${phone}_${consentType.Key}_${permissionType.Key}`;
                            permissionMap.set(key, {
                                ConsumerId,
                                Identifier: phone,
                                LegalEntityId: entityId,
                                Status: isSelected ? "CONFIRMED" : "WITHDRAWN",
                                PurposeItemIdentityTypes: permissionType.Key,
                                ConsentTypeId: consentType.Key
                            });
                        });
                    }
                });
            });
        });

        const payload = [...permissionMap.values()].map(item => {
            const typeKey = item.PurposeItemIdentityTypes;
            let match = null;
            if (typeof item.Identifier === "string" && item.Identifier.includes("@")) {
                match = initialConsents.Email?.find(e =>
                    e.Value === item.Identifier &&
                    e.LegalEntityId === item.LegalEntityId &&
                    e.ConsentTypeId === item.ConsentTypeId &&
                    e.PurposeItemIdentityType === typeKey
                );
            } else {
                match = initialConsents.Phone?.find(p =>
                    p.Value === item.Identifier &&
                    p.LegalEntityId === item.LegalEntityId &&
                    p.ConsentTypeId === item.ConsentTypeId &&
                    p.PurposeItemIdentityType === typeKey
                );
            }
            let isChanged = false;
            if (!match) {
                // Hiç kaydı olmayan bir izni geri çekmek değişiklik sayılmaz.
                isChanged = item.Status === "CONFIRMED";
            } else if (item.Status === "CONFIRMED" && !match.IsActive) {
                isChanged = true;
            } else if (item.Status === "WITHDRAWN" && match.IsActive) {
                isChanged = true;
            }
            return {
                ...item,
                IsChanged: isChanged
            };
        });

        // Sadece değişiklik yapılan legal entity'lerin LastAskedOn tarihi güncellenir.
        const askedLegalEntityIds = [...new Set(
            payload
                .filter(item => item.IsChanged)
                .map(item => item.LegalEntityId)
                .filter(entityId => Number.isInteger(entityId) && entityId > 0)
        )];

        dispatch(submitConsent({
            data: {
                consentRequests: payload,
                askedLegalEntityIds
            }
        })).then((action) => {
            if (submitConsent.fulfilled.match(action)) {
                dispatch(fetchConsentMetadata({ consumerId: ConsumerId }));
            }
            setIsSubmitting(false);
        });
    };

    return (
        <>
            <Typography variant="h4" className={classes.header}>
                {t('lblCommunicationPreferences')}
            </Typography>

            {error && (
                <Box mb={2}>
                    <Alert severity="error">
                        <Box>
                            {error}
                            {Array.isArray(errors) && errors.length > 0 && (
                                <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                                    {errors.map((e, idx) => (
                                        <li key={idx} style={{ marginLeft: 0 }}>{e.ErrorMessage}</li>
                                    ))}
                                </ul>
                            )}
                        </Box>
                    </Alert>
                </Box>
            )}
            {submitSuccess && (
                <Box mb={2}>
                    <Alert severity="success">{t('lblSuccess')}</Alert>
                </Box>
            )}
            {fetchMetaDataError && !legalEntities?.length && (
                <Box mb={2}>
                    <Alert severity="error">{t('lblLegalEntityNotFound')}</Alert>
                </Box>
            )}
            <Backdrop className={classes.backdrop} open={isSubmitting || loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Divider style={{ marginBottom: 16 }} />
            {(!fetchMetaDataError || (fetchMetaDataError && legalEntities?.length > 0)) && (
                <>
                    <Box mb={2}>
                        <FormLabel component="legend">{t('selectLegalEntity')}</FormLabel>

                        <Box className={`${classes.panel} ${classes.noScroll}`}>
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                wrap="nowrap"
                                spacing={2}
                                className={classes.gridFix}
                            >
                                <Grid item xs style={{ minWidth: 0 }}>
                                    <Box className={classes.noScroll}>
                                        <FormGroup row style={{ display: 'flex', flexWrap: 'wrap' }}>
                                            <Grid container spacing={2} wrap="wrap" className={classes.gridFix}>
                                                {legalEntities.map(entity => (
                                                    <Grid item key={entity.Id}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedEntities.includes(entity.Id)}
                                                                    onChange={handleEntitySelect(entity.Id)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={entity.Name}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </FormGroup>
                                    </Box>
                                </Grid>

                                {legalEntities && legalEntities.length > 1 && (
                                    <Grid item>
                                        <Button
                                            type="button"
                                            size="small"
                                            color="secondary"
                                            variant="contained"
                                            onClick={handleUpdateLegalEntity}
                                            style={{ whiteSpace: 'nowrap', minWidth: 140 }}
                                        >
                                            {t('btnUpdateLegalEntity')}
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                    {success !== false && (
                        <Grid container spacing={3}>
                            {legalEntities.map((entity) => {
                                return (
                                    <Grid item xs={12} md={6} key={entity.Id}>
                                        <Paper variant="outlined" style={{ padding: 16 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {entity.Name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                gutterBottom
                                                className={entity.LastAskedOn ? classes.lastAskedOn : classes.consentNotAskedYet}
                                            >
                                                {entity.LastAskedOn
                                                    ? `${t('lblLastAskedOn')}: ${moment(entity.LastAskedOn).format('DD/MM/YYYY')}`
                                                    : t('lblConsentNotAskedYet')}
                                            </Typography>
                                            {getConsentTypesForEntity(entity).map(consentType => (
                                                <Box key={consentType.Key} mt={2} mb={2} p={2} border={1} borderColor="grey.200" borderRadius={4}>
                                                    <Typography variant="subtitle1" gutterBottom>
                                                        {consentType.Label}
                                                    </Typography>
                                                    <FormGroup row>
                                                        {permissionTypes.map(permissionType => (
                                                            <FormControlLabel
                                                                key={permissionType.Key}
                                                                control={
                                                                    <Switch
                                                                        checked={entityConsent[entity.Id]?.[consentType.Key]?.[permissionType.Key]?.enabled || false}
                                                                        onChange={handlePermissionSwitch(entity.Id, consentType.Key, permissionType.Key)}
                                                                        color="primary"
                                                                    />
                                                                }
                                                                label={permissionType.Label}
                                                            />
                                                        ))}
                                                    </FormGroup>
                                                    {permissionTypes.map(permissionType => {
                                                        const isEmail = permissionType.Label.toLowerCase() === 'email';
                                                        const enabled = entityConsent[entity.Id]?.[consentType.Key]?.[permissionType.Key]?.enabled;
                                                        if (!enabled) return null;

                                                        if (isEmail) {
                                                            const hasValidEmail = Array.isArray(identifier?.Email) && identifier.Email.some(e => !!e && e.trim() !== '');
                                                            if (!hasValidEmail) {
                                                                return (
                                                                    <Box mt={1} key="no-email">
                                                                        <Alert severity="info">{t('noEmailDefined')}</Alert>
                                                                    </Box>
                                                                );
                                                            }
                                                            return (
                                                                <Box mt={1} key="email-checkboxes">
                                                                    <FormLabel component="legend">{t('emailAddresses')}</FormLabel>
                                                                    <FormGroup>
                                                                        {identifier.Email.filter(e => !!e && e.trim() !== '').map(emailValue => (
                                                                            <FormControlLabel
                                                                                key={emailValue}
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={entityConsent[entity.Id]?.[consentType.Key]?.[permissionType.Key]?.selected?.includes(emailValue) || false}
                                                                                        onChange={handleContactSelect(entity.Id, consentType.Key, permissionType.Key, emailValue)}
                                                                                        color="primary"
                                                                                    />
                                                                                }
                                                                                label={emailValue}
                                                                            />
                                                                        ))}
                                                                    </FormGroup>
                                                                </Box>
                                                            );
                                                        }
                                                        if (!isEmail) {
                                                            const hasValidPhone = Array.isArray(identifier?.Phone) && identifier.Phone.some(p => !!p && p.trim() !== '');
                                                            if (!hasValidPhone) {
                                                                return (
                                                                    <Box mt={1} key={`no-phone-${permissionType.Key}`}>
                                                                        <Alert severity="info">{t('noPhoneDefined')}</Alert>
                                                                    </Box>
                                                                );
                                                            }
                                                            return (
                                                                <Box mt={1} key={`phone-checkboxes-${permissionType.Key}`}>
                                                                    <FormLabel component="legend">{permissionType.Label} {t('lblPhones')}</FormLabel>
                                                                    <FormGroup>
                                                                        {identifier.Phone.filter(p => !!p && p.trim() !== '').map(phoneValue => (
                                                                            <FormControlLabel
                                                                                key={phoneValue + permissionType.Key}
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={entityConsent[entity.Id]?.[consentType.Key]?.[permissionType.Key]?.selected?.includes(phoneValue) || false}
                                                                                        onChange={handleContactSelect(entity.Id, consentType.Key, permissionType.Key, phoneValue)}
                                                                                        color="primary"
                                                                                    />
                                                                                }
                                                                                label={phoneValue}
                                                                            />
                                                                        ))}
                                                                    </FormGroup>
                                                                </Box>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                    {success !== false && (
                        <Button
                            type='button'
                            onClick={handleSubmit}
                            color="primary"
                            variant="contained"
                            size="large"
                            className={classes.submitButton}
                        >
                            {t('lblPost')}
                        </Button>
                    )}
                </>
            )}
        </>
    );
};

export default ConsentCore;
